import { faAngleRight } from '@fortawesome/pro-regular-svg-icons'
import {
  type BoxProps,
  Text,
  Breadcrumbs,
  Anchor,
  Skeleton,
} from '@mantine/core'
import Link from 'next/link'
import { useCallback } from 'react'

import { useQueryGetAllItemCategories } from '@flecto/core/store'
import { type ParameterID, type CoreItemCategory } from '@flecto/types'
import { Icon } from '@flecto/ui'

import { useStoreDetails } from '../../hooks/useStoreDetails'
import { useFlattenCategories } from '../../hooks/useStoreFlattenCategories'
import { useStorePathBuilder } from '../../hooks/useStorePathBuilder'

export type BreadCrumbItemCategoriesProps = BoxProps & {
  itemCategory?: CoreItemCategory
  isLoading?: boolean
}

export function BreadCrumbItemCategories({
  itemCategory,
  isLoading,
  ...props
}: BreadCrumbItemCategoriesProps) {
  if (isLoading) {
    return <Skeleton h={15} w={200} {...props} />
  }
  if (!itemCategory) {
    return
  }

  const url = useStorePathBuilder()
  const store = useStoreDetails()
  const { data } = useQueryGetAllItemCategories({
    storeDomain: store?.domain || '',
  })
  const categories = useFlattenCategories(data?.item_category || [])

  const getCategoriesBreadcrumb = useCallback(
    (categoryId: ParameterID): CoreItemCategory[] => {
      const breadcrumb: CoreItemCategory[] = []
      let currentId: number | undefined = Number(categoryId)

      while (currentId) {
        const category = categories?.find(
          (cat) => Number(currentId) === Number(cat.id)
        )

        if (category) {
          breadcrumb.push(category)
        }

        currentId = Number(category?.parent_id)
      }

      return breadcrumb.reverse()
    },
    [categories]
  )

  return (
    <Breadcrumbs
      separator={<Icon icon={faAngleRight} size='xs' color='gray.5' />}
      {...props}
    >
      {getCategoriesBreadcrumb(itemCategory.id).map((cat) => {
        if (!cat.parent_id) {
          return (
            <Text key={cat.id} c='dimmed'>
              {cat.name}
            </Text>
          )
        }

        return (
          <Anchor
            component={Link}
            href={url(`/categories/${cat.id}?category_ids=${cat.id}`)}
            key={cat.id}
          >
            {cat.name}
          </Anchor>
        )
      })}
    </Breadcrumbs>
  )
}
